/**
 * Pads a daily-bucketed time series so it spans the full selected period.
 *
 * Our timeseries hooks only emit buckets that had rows, so a range with data on
 * a few days collapses the categorical x-axis onto those days and a lone point
 * renders in a void. This utility overlays the returned points onto every
 * calendar day of the selected period, filling the gaps by an explicit policy:
 *
 * - `zero`  — counts: an empty day genuinely means 0, so the line stays
 *             continuous and dips to 0.
 * - `gap`   — averages/rates: an empty day has no value, so it becomes `null`
 *             and the line breaks across it (no interpolation, no fabricated 0).
 *
 * The frame is UTC on purpose. `getAnalyticsRangeUTC` treats the selected
 * yyyy-MM-dd range as UTC day boundaries, and Timescale returns each
 * `time_bucket` as a UTC `timestamp without time zone` (e.g.
 * "2026-07-14T00:00:00", no offset). Matching therefore happens on the UTC
 * calendar day, never the viewer's local day — projecting to the user's
 * timezone would drift a bucket onto the previous day for viewers west of UTC.
 */

export type FillPolicy = 'zero' | 'gap';

export interface PeriodRange {
  start: string;
  end: string;
}

/** A series point after padding: `value` is nullable (gap days) and lone real points are flagged. */
export type PaddedSeriesPoint<T> = Omit<T, 'value'> & {
  value: number | null;
  isIsolated?: boolean;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_TIMEZONE = /(Z|[+-]\d{2}:?\d{2})$/;
const MS_PER_DAY = 86_400_000;

/**
 * Runaway guard. Real analytics ranges top out around a year; anything larger
 * than this is treated as an invalid period and the series is returned
 * unpadded rather than fabricating tens of thousands of axis categories.
 */
const MAX_PERIOD_DAYS = 1000;

/**
 * Normalizes any bucket timestamp to its UTC calendar day as `yyyy-MM-dd`.
 * Date-only strings are already UTC day labels; ISO datetimes without an offset
 * are UTC (matching `getAnalyticsRangeUTC` and the DB's `timestamp without time
 * zone`), so we append `Z` before extracting the day. Returns "" if unparseable.
 */
const toUtcDayKey = (value: string): string => {
  const trimmed = value?.trim() ?? '';
  if (DATE_ONLY.test(trimmed)) {
    return trimmed;
  }
  const iso = HAS_TIMEZONE.test(trimmed) ? trimmed : `${trimmed}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

/**
 * Every UTC calendar day in [start, end] inclusive as `yyyy-MM-dd`.
 * Returns [] for an invalid, reversed, or unreasonably large period so callers
 * can fall back to the original series.
 */
const enumerateUtcDayKeys = (start: string, end: string): string[] => {
  const startKey = toUtcDayKey(start);
  const endKey = toUtcDayKey(end);
  if (!startKey || !endKey) {
    return [];
  }

  const cursor = new Date(`${startKey}T00:00:00.000Z`);
  const endTime = new Date(`${endKey}T00:00:00.000Z`).getTime();
  const spanDays = Math.round((endTime - cursor.getTime()) / MS_PER_DAY) + 1;
  if (spanDays <= 0 || spanDays > MAX_PERIOD_DAYS) {
    return [];
  }

  const keys: string[] = [];
  while (cursor.getTime() <= endTime) {
    keys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return keys;
};

/**
 * Overlays `series` onto every calendar day of `period`, filling empty days per
 * `fill`. Real points keep all their extra fields (e.g. `containmentRate`,
 * `csatBreakdown`); their `date` is rewritten to the normalized UTC-day key so
 * the categorical x-axis matches by exact string. Points outside the period are
 * dropped. An invalid/empty period returns the original series untouched.
 *
 * For `gap` only, a real value whose neighbours are both empty (or absent, at
 * the endpoints) is flagged `isIsolated` so the chart can render a marker for a
 * lone point that a broken line cannot draw on its own.
 */
export const padSeriesToPeriod = <T extends { date: string; value: number | null }>(
  series: readonly T[],
  period: PeriodRange,
  fill: FillPolicy
): Array<PaddedSeriesPoint<T>> => {
  const dayKeys = enumerateUtcDayKeys(period.start, period.end);
  if (dayKeys.length === 0) {
    return [...series] as Array<PaddedSeriesPoint<T>>;
  }

  // Index real points by their UTC calendar day. If the backend ever returns
  // two rows for one day (it does not today), the last one wins.
  const byDay = new Map<string, T>();
  for (const point of series) {
    const key = toUtcDayKey(point.date);
    if (key) {
      byDay.set(key, point);
    }
  }

  const filled: Array<PaddedSeriesPoint<T>> = dayKeys.map((day) => {
    const match = byDay.get(day);
    if (match) {
      return { ...match, date: day } as PaddedSeriesPoint<T>;
    }
    return { date: day, value: fill === 'zero' ? 0 : null } as PaddedSeriesPoint<T>;
  });

  if (fill === 'gap') {
    for (let i = 0; i < filled.length; i += 1) {
      const current = filled[i];
      if (current.value == null) {
        continue;
      }
      const prevEmpty = filled[i - 1] == null || filled[i - 1].value == null;
      const nextEmpty = filled[i + 1] == null || filled[i + 1].value == null;
      if (prevEmpty && nextEmpty) {
        current.isIsolated = true;
      }
    }
  }

  return filled;
};
